import React, { useState, useEffect } from "react";
import { Box, Button, Container, Typography, CircularProgress } from "@mui/material";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from 'react-toastify';
import { getSharingTemplateByIdApi, updateSharingTemplateApi } from "../services/sharingTemplate.service";
import MasterSharingTab from "./MasterSharingTab";
import MasterSharingCriteriaTab from "./MasterSharingCriteriaTab";

const EditSharingTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get('mode') === 'view';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [dataToSave, setDataToSave] = useState(null);

  // Fetch template data
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const data = await getSharingTemplateByIdApi(id);
        setTemplate(data);
        setDataToSave(data); // Initialize dataToSave with template data
      } catch (error) {
        console.error('Error fetching template:', error);
        toast.error('Failed to load template');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTemplate();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    console.log('Template data:', dataToSave);
    
    if (!template) {
      console.log('No template data, returning early');
      return;
    }

    setSaving(true);
    try {
      // Update the template with all form data
      const response = await updateSharingTemplateApi({
        ...dataToSave,
        _id: id
      });
      
      // Check if we got a response
      if (response) {
        // The API returns the updated template data
        toast.success('Template updated successfully');
        // Dispatch template update event
        const refreshEvent = new CustomEvent('templateUpdated', {
          detail: { id: id }
        });
        window.dispatchEvent(refreshEvent);
        // Navigate back to the table after a short delay
        setTimeout(() => {
          navigate(-1);
        }, 500);
      } else {
        toast.error('Failed to update template');
      }
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  const handleFormChange = (updatedData) => {
    // Update template state first
    setTemplate(prev => ({
      ...prev,
      ...updatedData
    }));
    // Then update dataToSave
    setDataToSave(prev => ({
      ...prev,
      ...updatedData
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!template) {
    return (
      <Box textAlign="center" p={3}>
        <Typography>Template not found</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              padding: 2,
              marginTop: 2,
              borderRadius: 2,
              marginRight: "90px",
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0,
                mb: "20px",
                color: "#5D6A82",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Open Sans",
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "29px",
                  letterSpacing: "0px",
                  color: "inherit",
                }}
              >
                {isViewMode ? "View Sharing Template" : "Edit Sharing Template"}
              </Typography>
            </Box>

          <Box>
            {activeTab === 0 ? (
              <MasterSharingTab 
                template={template} 
                onChange={handleFormChange}
                readOnly={isViewMode}
              />
            ) : (
              <MasterSharingCriteriaTab 
                template={template} 
                onChange={handleFormChange}
                readOnly={isViewMode}
              />
            )}
            {!isViewMode && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    handleSubmit(e);
                  }}
                  disabled={saving}
                  sx={{ minWidth: '150px' }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            )}
          </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default EditSharingTemplate;
